import { z } from 'zod';

/**
 * Setting-Gruppe Enum.
 * Organisiert Einstellungen nach Domain-Bereichen.
 */
export const SettingGroupSchema = z.enum(['general', 'mail', 'loyalty', 'registration']);

/** TypeScript Type für Setting-Gruppe */
export type SettingGroup = z.infer<typeof SettingGroupSchema>;

/**
 * Zod Schema für Setting — Runtime-Validierung + Type-Inference.
 * Key-Value Konfiguration mit Gruppierung.
 */
export const SettingSchema = z.object({
  key: z.string(),
  value: z.string(),
  group: SettingGroupSchema.default('general'),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type Setting = z.infer<typeof SettingSchema>;

/**
 * Schema für Setting-Erstellung.
 */
export const CreateSettingSchema = SettingSchema.pick({
  key: true,
  value: true,
  group: true,
});

/** TypeScript Type für Setting-Erstellung */
export type CreateSetting = z.infer<typeof CreateSettingSchema>;

/**
 * Schema für Setting-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateSettingSchema = SettingSchema.pick({
  value: true,
  group: true,
}).partial();

/** TypeScript Type für Setting-Update */
export type UpdateSetting = z.infer<typeof UpdateSettingSchema>;

/**
 * Schema für öffentliche Website-Einstellungen.
 * Wird vom Endpunkt GET /api/public/settings zurückgegeben.
 */
export const SiteSettingsSchema = z.object({
  /** Jahr der Vereinsgründung, z.B. "2009" */
  foundedYear: z.string().default('2009'),
  /** Anzahl der Mitglieder, z.B. "100+" */
  memberCount: z.string().default('100+'),
  /** Kurzversion / Claim, z.B. "24/7" */
  version: z.string().default('24/7'),
  /** Slogan der Faninitiative */
  slogan: z.string().default(''),
});

/** TypeScript Type für Website-Einstellungen */
export type SiteSettings = z.infer<typeof SiteSettingsSchema>;

/**
 * Schema für die Response von GET /api/public/settings.
 */
export const PublicSettingsResponseSchema = z.object({
  site: SiteSettingsSchema,
});

/** TypeScript Type für die Public-Settings-Response */
export type PublicSettingsResponse = z.infer<typeof PublicSettingsResponseSchema>;
